import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { RealtimeChannel } from "@supabase/supabase-js";

async function hashAccessToken(token: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(token);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

export function useSessionMonitor() {
  const [isDisconnected, setIsDisconnected] = useState(false);
  const tokenHashRef = useRef<string | null>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    let mounted = true;

    async function setup() {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session || !mounted) return;

      const userId = session.user.id;
      const accessToken = session.access_token;

      // Compute the hash of the current access token
      const hash = await hashAccessToken(accessToken);
      if (!mounted) return;
      tokenHashRef.current = hash;

      // Register this session with the edge function
      await supabase.functions.invoke("register-session", {
        body: {},
      });

      // Subscribe to realtime changes on user_sessions for this user
      const channel = supabase
        .channel(`session-monitor-${userId}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "user_sessions",
            filter: `user_id=eq.${userId}`,
          },
          (payload) => {
            if (!mounted) return;
            const updated = payload.new as {
              token_hash: string;
              is_active: boolean;
            };

            // If this session's token_hash was marked inactive, disconnect
            if (
              updated.token_hash === tokenHashRef.current &&
              updated.is_active === false
            ) {
              setIsDisconnected(true);
            }
          }
        )
        .subscribe();

      channelRef.current = channel;
    }

    setup();

    return () => {
      mounted = false;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, []);

  return { isDisconnected };
}
